import React, { useCallback, useContext, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import styled from "styled-components";
import { MainContext } from "../context/MainContext";
import fetchService from "../utils/fetch.service";
import TelegramLoginButton from "../components/TelegramAuth";
import { SEX_OPTIONS } from "../components/SearchPanel";
import DATA from "../const/data";

const Page = styled.section`
    width: min(100%, 720px);
    margin: 0 auto;

    .back-link {
        min-height: 44px;
        display: inline-flex;
        align-items: center;
        color: var(--text-soft);
        font-size: 14px;
        font-weight: 650;
    }

    h1 {
        margin: 10px 0 8px;
        font-family: Georgia, "Times New Roman", serif;
        font-size: clamp(36px, 10vw, 52px);
        line-height: 1;
        letter-spacing: -0.05em;
    }

    .intro {
        margin: 0 0 26px;
        color: var(--muted);
    }
`;

const Form = styled.div`
    padding: 20px;
    display: grid;
    gap: 26px;
    border: 1px solid var(--line-strong);
    border-radius: var(--radius-lg);
    background: rgba(17, 27, 43, 0.8);

    @media (min-width: 768px) { padding: 32px; }
`;

const Field = styled.div`
    label,
    legend {
        display: block;
        margin-bottom: 8px;
        color: var(--text);
        font-size: 14px;
        font-weight: 750;
    }

    input[type="text"],
    select {
        width: 100%;
        min-height: 48px;
        padding: 10px 13px;
        border: 1px solid var(--line-strong);
        border-radius: var(--radius-sm);
        background: rgba(8, 15, 27, 0.55);
        color: var(--text);
    }

    input:disabled { color: var(--muted); opacity: 1; }

    small {
        display: block;
        margin-top: 6px;
        color: var(--muted);
        font-size: 12px;
    }
`;

const ChoiceGroup = styled.fieldset`
    padding: 0;
    border: 0;
    margin: 0;

    > div {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 8px;
    }

    label {
        min-height: 50px;
        padding: 0 14px;
        display: flex;
        align-items: center;
        gap: 9px;
        border: 1px solid var(--line-strong);
        border-radius: var(--radius-sm);
        background: rgba(8, 15, 27, 0.35);
        cursor: pointer;
    }

    input { width: 19px; height: 19px; accent-color: var(--accent); }
`;

const Notice = styled.div`
    padding: 14px;
    border: 1px solid ${ props => props.$danger ? "rgba(251, 113, 133, 0.32)" : "var(--line)" };
    border-radius: var(--radius-sm);
    background: ${ props => props.$danger ? "rgba(251, 113, 133, 0.07)" : "rgba(255, 255, 255, 0.02)" };
    color: var(--text-soft);
    font-size: 13px;

    p { margin: 0 0 12px; }
`;

const RangeField = styled.div`
    display: grid;
    gap: 10px;

    div { display: flex; justify-content: space-between; gap: 16px; }
    label { color: var(--text); font-size: 14px; font-weight: 750; }
    strong { color: var(--accent-strong); }
    input { width: 100%; min-height: 44px; accent-color: var(--accent); }
`;

const Toggle = styled.label`
    min-height: 60px;
    display: flex;
    align-items: flex-start;
    gap: 12px;
    cursor: pointer;

    input {
        width: 22px;
        height: 22px;
        margin-top: 2px;
        flex: 0 0 auto;
        accent-color: var(--accent);
    }

    span { color: var(--text-soft); font-size: 14px; }
`;

const PrimaryButton = styled.button`
    width: 100%;
    min-height: 54px;
    border-radius: 13px;
    background: var(--accent);
    color: #24101a;
    font-weight: 800;
    cursor: pointer;

    &:hover:not(:disabled) { background: var(--accent-strong); }
    &:disabled { opacity: 0.45; cursor: not-allowed; }
`;

const DangerZone = styled.section`
    margin-top: 28px;
    padding: 20px;
    border: 1px solid rgba(251, 113, 133, 0.26);
    border-radius: var(--radius-md);

    h2 { margin: 0 0 5px; font-size: 16px; }
    p { margin: 0 0 14px; color: var(--muted); font-size: 13px; }
    button {
        min-height: 44px;
        padding: 0 16px;
        border: 1px solid rgba(251, 113, 133, 0.45);
        border-radius: var(--radius-sm);
        background: transparent;
        color: #fda4af;
        font-weight: 750;
        cursor: pointer;
    }
`;

const Modal = styled.div`
    position: fixed;
    inset: 0;
    z-index: 50;
    padding: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(3, 7, 13, 0.82);

    > div {
        width: min(100%, 400px);
        padding: 24px;
        border: 1px solid var(--line-strong);
        border-radius: var(--radius-lg);
        background: #111b2b;
        box-shadow: var(--shadow-lg);
    }

    h2 { margin: 0 0 8px; }
    p { margin: 0 0 20px; color: var(--text-soft); }
    .actions { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; }
    button { min-height: 46px; border-radius: var(--radius-sm); font-weight: 750; cursor: pointer; }
    .cancel { background: var(--surface-raised); color: var(--text); }
    .confirm { background: var(--danger); color: #260b10; }
`;

const MyProfile = () => {
    document.title = `Profile${ DATA.websiteTitle }`;
    const navigate = useNavigate();
    const { userContext, profilesContext, searchedContext } = useContext(MainContext);
    const [user, setUser] = userContext;
    const [, setProfiles] = profilesContext;
    const [, setSearched] = searchedContext;
    const [displayName, setDisplayName] = useState(user.displayName || "");
    const [username] = useState(user.username || "");
    const [gender, setGender] = useState(user.gender || "man");
    const [orientation, setOrientation] = useState(user.orientation || "straight");
    const [age, setAge] = useState(user.age || 22);
    const [displayOnSearches, setDisplayOnSearches] = useState(user.displayOnSearch !== false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    const onTelegramAuth = useCallback(async (telegramUser) => {
        fetchService.post("/auth", telegramUser).then((res) => {
            if (res.data?.user) setUser(res.data.user);
        }).catch((error) => console.log(error));
    }, [setUser]);

    const save = async () => {
        setIsSaving(true);
        const updatedData = await fetchService.put("/user", {
            displayName,
            gender,
            orientation,
            age: parseInt(age, 10),
            displayOnSearch: displayOnSearches
        });
        if (updatedData.data) setUser(updatedData.data);
        setSearched(false);
        setProfiles([]);
        setIsSaving(false);
        navigate("/", { replace: true });
    };

    const deleteAccount = async () => {
        setIsDeleting(true);
        try {
            await fetchService.post("/user-d");
            setUser(null);
            setSearched(false);
            setProfiles([]);
            navigate("/login", { replace: true });
        } catch (error) {
            setIsDeleting(false);
        }
    };

    const availableOrientations = SEX_OPTIONS.filter((option) => option.klo.includes(gender));
    const saveDisabled = !displayName || !username || !gender || !orientation || !age || isSaving;

    return (
        <Page>
            <Link className="back-link" to="/">← Back to search</Link>
            <h1>My profile</h1>
            <p className="intro">Keep your details accurate so people know who they are meeting.</p>

            <Form>
                <Field>
                    <label htmlFor="display-name">Display name</label>
                    <input id="display-name" type="text" value={ displayName } onChange={ event => setDisplayName(event.target.value) }/>
                    <small>This is how your name appears in search results.</small>
                </Field>

                <Field>
                    <label htmlFor="telegram-username">Telegram username</label>
                    <input id="telegram-username" type="text" value={ username } disabled/>
                    <small>People use this username to contact you on Telegram.</small>
                </Field>

                { !username && (
                    <Notice $danger>
                        <p>Set a username in Telegram, then sign in again to refresh it.</p>
                        <TelegramLoginButton dataOnauth={ onTelegramAuth } botName="tele_sex_chat_bot" buttonSize="medium"/>
                    </Notice>
                ) }

                <ChoiceGroup>
                    <legend>I am</legend>
                    <div>
                        { ["woman", "man"].map((value) => (
                            <label key={ value }>
                                <input
                                    type="radio"
                                    name="gender"
                                    disabled={ user.po }
                                    checked={ gender === value }
                                    onChange={ () => !user.po && setGender(value) }
                                />
                                { value === "woman" ? "Woman" : "Man" }
                            </label>
                        )) }
                    </div>
                </ChoiceGroup>

                { gender === "woman" && !user.po && (
                    <Notice><Link to="/verify">Verify your gender</Link> to unlock the verified-only filter.</Notice>
                ) }

                <Field>
                    <label htmlFor="orientation">Orientation</label>
                    <select id="orientation" value={ orientation } onChange={ event => setOrientation(event.target.value) }>
                        { availableOrientations.map((option) => (
                            <option key={ option.value } value={ option.value }>{ option.text }</option>
                        )) }
                    </select>
                </Field>

                <RangeField>
                    <div><label htmlFor="profile-age">Age</label><strong>{ age }</strong></div>
                    <input id="profile-age" type="range" min="18" max="90" value={ age } onChange={ event => setAge(event.target.value) }/>
                </RangeField>

                <Toggle>
                    <input type="checkbox" checked={ displayOnSearches } onChange={ () => setDisplayOnSearches((value) => !value) }/>
                    <span>Show my profile in search results. This must be enabled to search for other people.</span>
                </Toggle>

                <PrimaryButton type="button" disabled={ saveDisabled } onClick={ save }>
                    { isSaving ? "Saving…" : "Save profile" }
                </PrimaryButton>
            </Form>

            <DangerZone>
                <h2>Delete account</h2>
                <p>Permanently remove your profile and account data.</p>
                <button type="button" onClick={ () => setShowDeleteModal(true) }>Delete account</button>
            </DangerZone>

            { showDeleteModal && (
                <Modal role="dialog" aria-modal="true" aria-labelledby="delete-title">
                    <div>
                        <h2 id="delete-title">Delete your account?</h2>
                        <p>This action cannot be undone.</p>
                        <div className="actions">
                            <button className="cancel" type="button" onClick={ () => setShowDeleteModal(false) }>Cancel</button>
                            <button className="confirm" type="button" disabled={ isDeleting } onClick={ deleteAccount }>
                                { isDeleting ? "Deleting…" : "Delete" }
                            </button>
                        </div>
                    </div>
                </Modal>
            ) }
        </Page>
    );
};

export default MyProfile;
